import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/pro-light-svg-icons/faEllipsisVertical';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../common/types';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { SongLyricsView, SongPageSection } from './song/page/catalogSongPath';
import { songPageMessages } from './song/page/songPageMessages';
import { mergeMessages } from './merge/mergeMessages';

/** What a row and its menu can do (design song-page a2). */
export interface CatalogRowMenuActions {
  /** Opens the song page, at a section when given. */
  onOpenSong: (item: CatalogItem, section?: SongPageSection, view?: SongLyricsView) => void;
  onMakeInStudio: (item: CatalogItem) => void;
  /** The song page of the row in Releases. */
  onOpenRelease: (item: CatalogItem) => void;
  onCopyLink: (item: CatalogItem) => void;
  onRemove: (item: CatalogItem) => void;
  /** "Merge with another song…" (design catalog-merge a1). Undefined: the item is not shown. */
  onMerge?: (item: CatalogItem) => void;
}

interface CatalogRowMenuProps extends CatalogRowMenuActions {
  item: CatalogItem;
}

const rowMenuMessages = defineMessages({
  open: { id: 'app.sml.catalog.row.menu.open', defaultMessage: 'Open the song page' },
});

/**
 * The shortcuts of a row (design song-page a2). The song page is the default target of the row;
 * the ownership and the service links moved to the page (Details, Links and release).
 */
const CatalogRowMenuComponent: React.FC<CatalogRowMenuProps> = ({
  item,
  onOpenSong,
  onMakeInStudio,
  onOpenRelease,
  onCopyLink,
  onRemove,
  onMerge,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => setIsOpen((open) => !open), []);
  const open = useCallback(() => onOpenSong(item), [item, onOpenSong]);
  const editLyrics = useCallback(() => onOpenSong(item, 'lyrics', 'text'), [item, onOpenSong]);
  const studio = useCallback(() => onMakeInStudio(item), [item, onMakeInStudio]);
  const release = useCallback(() => onOpenRelease(item), [item, onOpenRelease]);
  const copy = useCallback(() => onCopyLink(item), [item, onCopyLink]);
  const remove = useCallback(() => onRemove(item), [item, onRemove]);
  const merge = useCallback(() => onMerge?.(item), [item, onMerge]);

  const isClaimed = item.kind === CatalogItemKind.claimed;

  return (
    <Dropdown isOpen={isOpen} toggle={toggle} direction="down">
      <DropdownToggle
        color="link"
        className="p-0 text-decoration-none"
        aria-label={intl.formatMessage({ id: 'app.sml.catalog.row.menu', defaultMessage: 'Release actions' })}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem onClick={open}>{intl.formatMessage(rowMenuMessages.open)}</DropdownItem>
        <DropdownItem onClick={editLyrics}>{intl.formatMessage(songPageMessages.menuEditLyrics)}</DropdownItem>
        <DropdownItem onClick={studio}>{intl.formatMessage(songPageMessages.makeInStudio)}</DropdownItem>
        <DropdownItem onClick={release}>{intl.formatMessage(songPageMessages.openInReleases)}</DropdownItem>
        <DropdownItem divider />
        <DropdownItem onClick={copy}>{intl.formatMessage(songPageMessages.menuCopyLink)}</DropdownItem>
        {onMerge !== undefined && <DropdownItem onClick={merge}>{intl.formatMessage(mergeMessages.menu)}</DropdownItem>}
        <DropdownItem divider />
        <DropdownItem className="text-danger" onClick={remove}>
          {intl.formatMessage(isClaimed ? songPageMessages.menuUnclaim : songPageMessages.menuRemove)}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export const CatalogRowMenu = TypedMemo(CatalogRowMenuComponent);
